/**
 * Phone Number Utilities
 * Handles phone number normalization and formatting for international support
 */

/**
 * Normalize phone number to E.164 format (+2348012345678)
 * Handles various input formats intelligently for user-friendly experience
 * 
 * @param {string} phone - Phone number in any format
 * @param {string} defaultCountryCode - Default country code (e.g., '234' for Nigeria)
 * @returns {string|null} - Normalized phone in E.164 format or null if invalid
 * 
 * @example
 * normalizePhoneNumber('08012345678', '234') // Returns: '+2348012345678'
 * normalizePhoneNumber('2348012345678', '234') // Returns: '+2348012345678'
 * normalizePhoneNumber('+2348012345678', '234') // Returns: '+2348012345678'
 */
export const normalizePhoneNumber = (phone, defaultCountryCode = '234') => {
  if (!phone || typeof phone !== 'string') return null;
  
  // Remove all spaces, dashes, parentheses
  let cleaned = phone.replace(/[\s\-()]/g, '');
  
  // If already in E.164 format (+234...), return as is
  if (cleaned.startsWith('+')) {
    // Validate E.164 format
    if (/^\+[1-9]\d{1,14}$/.test(cleaned)) {
      return cleaned;
    }
    return null; // Invalid format
  }
  
  // If starts with country code without + (2348012345678)
  if (cleaned.startsWith('234') && cleaned.length >= 12) {
    return '+' + cleaned;
  }
  
  // If starts with 0 (local format: 08012345678)
  if (cleaned.startsWith('0')) {
    // Remove leading 0 and add country code
    const localNumber = cleaned.substring(1);
    if (localNumber.length >= 10) {
      return '+' + defaultCountryCode + localNumber;
    }
    return null; // Too short
  }
  
  // If it's just digits (8012345678) - assume local format
  if (/^\d+$/.test(cleaned)) {
    // If length is 10-11 digits, assume local format
    if (cleaned.length >= 10 && cleaned.length <= 11) {
      // Remove leading 0 if present
      if (cleaned.startsWith('0')) {
        cleaned = cleaned.substring(1);
      }
      return '+' + defaultCountryCode + cleaned;
    }
    
    // If longer, might already include country code
    if (cleaned.length > 11) {
      // Check if it starts with country code
      if (cleaned.startsWith(defaultCountryCode)) {
        return '+' + cleaned;
      }
      // Otherwise, might be international without +
      // Try to validate as E.164 without +
      if (/^[1-9]\d{1,14}$/.test(cleaned)) {
        return '+' + cleaned;
      }
    }
  }
  
  // Return null if we can't determine valid format
  return null;
};

/**
 * Extract country code from phone number
 * 
 * @param {string} phone - Phone number in E.164 format
 * @returns {string|null} - Country code (e.g., '+234') or null
 * 
 * @example
 * extractCountryCode('+2348012345678') // Returns: '+234'
 * extractCountryCode('+233123456789') // Returns: '+233'
 */
export const extractCountryCode = (phone) => {
  if (!phone || !phone.startsWith('+')) return null;
  
  // Common country codes (1-3 digits after +)
  const match = phone.match(/^\+(\d{1,3})/);
  return match ? '+' + match[1] : null;
};

/**
 * Format phone for display (user-friendly format)
 * 
 * @param {string} phone - Phone number in E.164 format
 * @returns {string} - Formatted phone number for display
 * 
 * @example
 * formatPhoneForDisplay('+2348012345678') // Returns: '+234 801 234 5678'
 * formatPhoneForDisplay('+233123456789') // Returns: '+233 123 456 789'
 */
export const formatPhoneForDisplay = (phone) => {
  if (!phone) return '';
  
  const normalized = normalizePhoneNumber(phone);
  if (!normalized) return phone; // Return original if can't normalize
  
  // Format: +234 801 234 5678
  if (normalized.startsWith('+234')) {
    const number = normalized.substring(4);
    if (number.length === 10) {
      return `+234 ${number.substring(0, 3)} ${number.substring(3, 6)} ${number.substring(6)}`;
    }
  }
  
  // Generic formatting for other countries
  const countryCode = extractCountryCode(normalized);
  if (countryCode) {
    const number = normalized.substring(countryCode.length);
    // Format based on number length
    if (number.length <= 10) {
      return `${countryCode} ${number}`;
    }
    // For longer numbers, add spacing every 3-4 digits
    return `${countryCode} ${number.match(/.{1,4}/g)?.join(' ') || number}`;
  }
  
  return normalized;
};

/**
 * Validate phone number format
 * 
 * @param {string} phone - Phone number in any format
 * @param {string} defaultCountryCode - Default country code
 * @returns {boolean} - True if valid phone format
 */
export const isValidPhoneFormat = (phone, defaultCountryCode = '234') => {
  if (!phone) return false;
  
  const normalized = normalizePhoneNumber(phone, defaultCountryCode);
  if (!normalized) return false;
  
  // E.164 format: + followed by 1-15 digits, starting with 1-9
  return /^\+[1-9]\d{1,14}$/.test(normalized);
};

/**
 * Get local number from E.164 format (remove country code)
 * 
 * @param {string} phone - Phone number in E.164 format
 * @param {string} countryCode - Country code to remove (e.g., '+234')
 * @returns {string} - Local number without country code
 * 
 * @example
 * getLocalNumber('+2348012345678', '+234') // Returns: '8012345678'
 */
export const getLocalNumber = (phone, countryCode = '+234') => {
  if (!phone) return '';
  
  if (phone.startsWith(countryCode)) {
    return phone.substring(countryCode.length);
  }
  
  return phone;
};

